/**
 * comprobante-lote-parser.ts — PURE row mapper + shape validator (design.md
 * D7/D7b). This is the FIRST validation stage: turns an already-mapped JSON
 * array (this change ships no UI — no XLSX decoding enters the backend,
 * design.md D7) into typed `ComprobanteLoteRow`s, rejecting malformed shapes
 * before any DB round trip. The SECOND stage —
 * `validateLedgerMovementBatch` (packages/modules/sumi/src/domain/cuentas-pagar)
 * — needs DB-loaded context (known supplier ids, existing llaves) that this
 * pure shape check does not have, so duplicate-key / unknown-CUIT rejection
 * happens there, not here.
 *
 * ONE shape for every bulk load: normal mass import AND opening balance are
 * the same rows with `origen='importado'` — the opening ones simply carry an
 * old `fechaEmision` (AP-3.3, "no existe una regla especial de saldo
 * inicial"). Mold: item-master-parser.ts:1-60.
 *
 * The supplier comes as `supplierId` (the SPA resolves the name the tesorero
 * typed, or lets them pick it — 2026-10-24: they don't know CUITs, and some
 * suppliers have none) or as `supplierCuit` (API callers). Messages are the
 * ones the tesorero reads in the pre-flight report, so they are plain Spanish.
 *
 * Spec: openspec/changes/add-cuentas-pagar/specs/cuentas-pagar-ledger/spec.md AP-3, AP-11.2
 * Design: openspec/changes/add-cuentas-pagar/design.md D7, D7b
 */
import { z } from 'zod';
import { CURRENCY_CODES } from '@suite/contracts-kernel';
import { COMPROBANTE_TIPOS } from './vocab.js';

const isDate = (s: string) => s !== '' && !Number.isNaN(new Date(s).getTime());

export const ComprobanteLoteRowSchema = z
  .object({
    /** sumi.supplier.id — what the SPA sends after resolving the name typed in the file. */
    supplierId: z.string().uuid('Proveedor inválido').optional(),
    /** Digits or formatted — normalised by the caller (contracts-kernel normalizeCuit). */
    supplierCuit: z.string().min(1).optional(),
    tipo: z.enum(COMPROBANTE_TIPOS, {
      errorMap: () => ({ message: 'Tipo no reconocido: usá Factura, Nota de crédito o Nota de débito.' }),
    }),
    puntoVenta: z.string().min(1, 'Falta el punto de venta.'),
    numero: z.string().min(1, 'Falta el número.'),
    moneda: z.enum(CURRENCY_CODES, { errorMap: () => ({ message: `Moneda no reconocida: usá Pesos o Dólares.` }) }),
    monto: z.number({ invalid_type_error: 'El monto total no es un número.' }).positive('El monto total tiene que ser mayor a cero.'),
    /** APR-2.4 — optional net (magnitude). Absent -> stored NULL; the server never suggests one. */
    montoNeto: z.number({ invalid_type_error: 'El monto neto no es un número.' }).nonnegative('El monto neto no puede ser negativo.').optional(),
    /** ISO date string (YYYY-MM-DD). */
    fechaEmision: z.string().refine(isDate, 'Fecha de emisión inválida.'),
    fechaVencimiento: z.string().refine(isDate, 'Fecha de vencimiento inválida.').optional(),
  })
  .refine((v) => v.supplierId !== undefined || v.supplierCuit !== undefined, {
    message: 'Falta el proveedor.',
    path: ['supplierId'],
  })
  .refine((v) => v.montoNeto === undefined || v.montoNeto <= v.monto, {
    message: 'El neto no puede superar al total.',
    path: ['montoNeto'],
  });

export type ComprobanteLoteRow = z.infer<typeof ComprobanteLoteRowSchema>;

/**
 * Response contract of `POST /comprobantes/lote` — APR-2.1, literal shape.
 */
export const ComprobanteLoteResponseSchema = z.object({
  written: z.number().int().nonnegative(),
  rejected: z.array(z.object({ index: z.number().int(), reason: z.string() })),
});
export type ComprobanteLoteResponse = z.infer<typeof ComprobanteLoteResponseSchema>;

export interface ParsedComprobanteLoteRows {
  readonly valid: ComprobanteLoteRow[];
  readonly rejected: { readonly row: number; readonly reason: string }[];
}

/**
 * Pure, no DB. Splits raw untyped input into shape-valid rows and rejected
 * ones with a per-row reason. Does NOT check duplicate llaves or unknown
 * CUITs — that is `validateLedgerMovementBatch`'s job (needs DB context).
 */
export function parseComprobanteLoteRows(raw: unknown[]): ParsedComprobanteLoteRows {
  const valid: ComprobanteLoteRow[] = [];
  const rejected: { row: number; reason: string }[] = [];

  raw.forEach((candidate, row) => {
    const result = ComprobanteLoteRowSchema.safeParse(candidate);
    if (result.success) {
      valid.push(result.data);
    } else {
      rejected.push({ row, reason: result.error.issues[0]?.message ?? 'fila inválida' });
    }
  });

  return { valid, rejected };
}
